import {
    Component, forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren
} from '@angular/core';
import {forkJoin, Subscription} from 'rxjs';
import {
    ApplicantModel,
    ApplicationModel,
    KeyIdentifierModel
} from '@service/models';
import {ApplicantType} from '@service/enum/process-state.enum';
import { NgForm, NgModel} from '@angular/forms';
import { AppConfigService } from '@service/config/app-config.service';
import {ApplicantService, ApplicationService} from '@service/services';
import {SharedkeyDataService} from '@service/services/common-services/sharedkey-data.service';
import {environment} from '@env/environment';
import {ExtendedAttributesModel} from '@service/models/extendedAttributes.model';
import {PhoneNumberOptionModel} from '@shared/form-components/phone-number/phone-number-option.model';

@Component({
    selector: 'creditsnap-custom-questions',
    templateUrl: './custom-questions.component.html',
    styleUrls: ['./custom-questions.component.scss']
})
export class CustomQuestionsComponent implements OnInit, OnDestroy, OnChanges {

    @ViewChildren(NgModel) ngModels: QueryList<NgModel>;
    @ViewChild('customQuestionsFormModel') customQuestionsFormModel: NgForm;
    @Input() application: ApplicationModel;
    @Input() applicant: ApplicantModel;
    @Input() phoneOptions: PhoneNumberOptionModel = new PhoneNumberOptionModel();
    @Input() selectedSubProducts = [];
    @Input('data-test-id') testId: string = 'custom-questions';
    protected readonly ApplicantType = ApplicantType;

    subscription: Subscription;
    applicantName = '';
    applicantConsent: Subscription;
    keyIdentifier: KeyIdentifierModel;
    environment = environment;
    institutionId = this.environment.institutionId;
    appConfig: any = {};
    finalSubmitPageConfig: any = {};
    declarationQuestions: any = [];
    assetsQuestions: any = [];
    customQuestions = [];
    selectedQuestionsConfig = [];
    selectedNameYourOwnClubSavingsProduct: any[] = [];
    selectedCheckingAccountList: any[] = [];
    constructor(
        private sharedKeyDataService: SharedkeyDataService,
        private applicationService: ApplicationService,
        private applicantService: ApplicantService,) {
        this.appConfig = AppConfigService.config;
        this.subscription = this.sharedKeyDataService.keyIdentifier$
            .subscribe((data) => {
                this.keyIdentifier = data;
            });
    }

    ngOnInit() {
        this.assignQuestions();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['applicant'] && changes['applicant'].currentValue) {
            this.applicant = changes['applicant'].currentValue;
            this.loadData();
        }
    }

    loadData() {
        if (!this.applicant) {
            return;
        }
        this.applicantName = `(${this.applicant.firstName} ${this.applicant.lastName})`;
    }

    mergeMultipleCustomQuestions(customQuestionsConfigs: any[]) {
        // Flatten all customQuestions arrays from all configs
        const mergedCustomQuestions = customQuestionsConfigs.flatMap(cfg => cfg?.customQuestions || []);
        const uniqueCustomQuestions = Array.from(
            new Map(mergedCustomQuestions.map(q => [q.question, q])).values()
        );

        // Merge all top-level properties
        const mergedTopLevel = Object.assign({}, ...customQuestionsConfigs);

        //  final merged config
        return {
            ...mergedTopLevel,
            customQuestions: uniqueCustomQuestions,
        };
    }

    assignQuestions() {
        let questionsConfig;
        console.log('selectedSubproduct', this.selectedSubProducts);
        const selectedTypes: string[] = [
            ...new Set(this.selectedSubProducts.map(offer => offer.subProductName))
        ];
        this.selectedNameYourOwnClubSavingsProduct = this.selectedSubProducts.filter(
            subProduct => subProduct.subProductName === 'SAV' && subProduct.subProductSubType === 'Name_Your_Own_Club_Savings'
        );
        this.selectedCheckingAccountList = this.selectedSubProducts.filter(
            subProduct => subProduct.subProductName === 'CHK'
        );
        if (!selectedTypes.includes(this.keyIdentifier.loanPurpose)) {
            selectedTypes.push(this.keyIdentifier.loanPurpose);
        }

        selectedTypes.forEach(type => {
            const config = this.appConfig.loanPurpose[type]?.questionsConfig;

            if (config?.customQuestions?.length) {
                this.selectedQuestionsConfig.push(config);
            }
        });
        questionsConfig = this.mergeMultipleCustomQuestions(this.selectedQuestionsConfig);

        // Retrieve previously selected answers from extendedAttributes (if application exists)
        const savedAnswers = Array.isArray(this.keyIdentifier.application?.extendedAttributes) ?
            this.applicant.extendedAttributes : [];

        const applicantSavedAnswers = Array.isArray(this.applicant?.extendedAttributes) ? this.applicant.extendedAttributes : [];
        const applicationSavedAnswers = Array.isArray(this.application?.extendedAttributes) ? this.application.extendedAttributes : [];


        this.declarationQuestions = (questionsConfig?.declarationQuestions || []).map(question => {
            const savedAnswer = savedAnswers.find(attr => attr.name === question.question)?.value;
            return {...question, answer: savedAnswer || question.answer || ''};
        });

        this.assetsQuestions = (questionsConfig?.assetsQuestions || []).map(question => {
            const savedAnswer = savedAnswers.find(attr => attr.name === question.question)?.value;
            return {...question, answer: savedAnswer || question.answer || ''};
        });

        this.customQuestions = (questionsConfig?.customQuestions || []).
            filter(question => this.isConditionMatched(question.condition))
            .filter(question => this.shouldShowQuestion(question))
        .map(question => {
            const isApplicationLevel = question.visibility !== 'APPLICANT';
            const savedAnswers = isApplicationLevel ? applicationSavedAnswers : applicantSavedAnswers;
            const savedAnswer = savedAnswers.find(attr => attr.name === question.question)?.value;
            let answer = question.answer;
            if (savedAnswer === '1') {
                answer = true;
            } else if (savedAnswer === '0') {
                answer = false;
            } else if (question.type === 'multi-checkbox' && savedAnswer) {
                answer = savedAnswer.split(';').map(val => val.trim());
            } else if (question.type === 'multi-checkbox' && !savedAnswer) {
                answer = [];
            } else {
                answer = savedAnswer || question.answer || '';
            }

            const updatedQuestion = { ...question, answer };

            if (updatedQuestion.subQuestions) {
                updatedQuestion.subQuestions = updatedQuestion.subQuestions.map(subQuestion => {
                    const savedSubAnswer = savedAnswers.find(attr => attr.name === subQuestion.question)?.value;
                    let subAnswer = subQuestion.answer;
                    if (savedSubAnswer === '1') {
                        subAnswer = true;
                    } else if (savedSubAnswer === '0') {
                        subAnswer = false;
                    } else if (subQuestion.type === 'multi-checkbox' && savedSubAnswer) {
                        subAnswer = savedSubAnswer.split(';').map(val => val.trim());
                    } else if (subQuestion.type === 'multi-checkbox' && !savedSubAnswer) {
                        subAnswer = [];
                    } else {
                        subAnswer = savedSubAnswer || subQuestion.answer || '';
                    }

                    const updatedSubQuestion = { ...subQuestion, answer: subAnswer };

                    if (updatedSubQuestion.subQuestions) {
                        updatedSubQuestion.subQuestions = updatedSubQuestion.subQuestions.map(grandSubQuestion => {
                            const savedGrandSubAnswer = savedAnswers.find(attr => attr.name === grandSubQuestion.question)?.value;
                            let grandAnswer = grandSubQuestion.answer;

                            if (savedGrandSubAnswer === '1') {
                                grandAnswer = true;
                            }
                            else if (savedGrandSubAnswer === '0') {
                                grandAnswer = false;
                            }
                            else if (grandSubQuestion.type === 'multi-checkbox' && savedGrandSubAnswer) {
                                grandAnswer = savedGrandSubAnswer.split(';').map(val => val.trim());
                            }
                            else if (grandSubQuestion.type === 'multi-checkbox' && !savedGrandSubAnswer) {
                                grandAnswer = [];
                            }
                            else {
                                grandAnswer = savedGrandSubAnswer || grandSubQuestion.answer || '';
                            }

                            return { ...grandSubQuestion, answer: grandAnswer };
                        });
                    }

                    return updatedSubQuestion;
                });
            }

            return updatedQuestion;
        });
        console.log('CQ', this.customQuestions);
    }

    private isConditionMatched(condition: any): boolean {
        if (!condition) {
            return true;
        }

        const {
            referralCode: conditionReferral,
            empStatus: conditionEmpStatus,
            empType: conditionEmpType,
            isNotFamilyMember: conditionIsNotFamilyMember,
            customerIdentifierNull: conditionForCustomerIdentifierNull,
            customerIdentifierNotNull: conditionForCustomerIdentifierNotNull,
            checkingSubProductSelected: conditionForCheckingSubProductSelected,
            nameYourOwnClubSavingsSelected: conditionForNameYourOwnClubSavings
        } = condition;

        let isReferralCodeMatch = true;
        let isEmployerConditionMatched = true;
        let isNotFamilyMember = true;
        let isCustomerIdentifierNull = true;
        let isCustomerIdentifierNotNull = true;
        let isCheckingSubProductSelected = true;
        let isNameYourOwnClubSavingsSelected = true;

        if (conditionReferral) {
            isReferralCodeMatch = conditionReferral.includes(this.application.referralCode || null);
        }

        if (conditionEmpStatus || conditionEmpType) {
            isEmployerConditionMatched = this.application.applicants.some(applicant =>
                applicant.employers?.some(employer => {
                    const isEmpStatusMatch = conditionEmpStatus ? conditionEmpStatus.includes(employer.empStatus) : true;
                    const isEmpTypeMatch = conditionEmpType ? conditionEmpType.includes(employer.empType) : true;
                    return isEmpStatusMatch && isEmpTypeMatch;
                })
            ) ?? false;
        }

        const primaryApplicant = this.application.applicants
            ?.find(app => app.applicantType === 'P');

        /** true when the primary applicant exists AND its customerIdentifier is null */
        if (conditionForCustomerIdentifierNull) {
            isCustomerIdentifierNull = !!primaryApplicant && primaryApplicant.customerIdentifier == null;
        }

        if (conditionIsNotFamilyMember) {
            isNotFamilyMember = !!primaryApplicant && primaryApplicant.membershipEligibilityCriteria !== 'FAMILY_MEMBER';
        }

        /** true when the primary applicant exists AND its customerIdentifier is NOT null */
        if (conditionForCustomerIdentifierNotNull) {
            isCustomerIdentifierNotNull = !!primaryApplicant && primaryApplicant.customerIdentifier != null;
        }

        if (conditionForCheckingSubProductSelected) {
            isCheckingSubProductSelected = this.selectedCheckingAccountList?.length > 0;
        }

        if (conditionForNameYourOwnClubSavings) {
            isNameYourOwnClubSavingsSelected = this.selectedNameYourOwnClubSavingsProduct?.length > 0;
        }

        return (
            isReferralCodeMatch &&
            isEmployerConditionMatched &&
            isNotFamilyMember &&
            isCustomerIdentifierNull &&
            isCustomerIdentifierNotNull &&
            isCheckingSubProductSelected &&
            isNameYourOwnClubSavingsSelected
        );
    }


    validateAndSubmitForm() {
        return new Promise((resolve, reject) => {
            this.ngModels.forEach((ngModel) => {
                ngModel.control.markAsTouched();
                ngModel.control.updateValueAndValidity();
            });

            let multiCheckboxInvalid = false;

            this.customQuestions.forEach(q => {
                if (q.type === 'multi-checkbox' && q.required) {
                    q.touched = true;

                    let values: string[] = [];

                    if (Array.isArray(q.answer)) {
                        values = q.answer;
                    } else if (typeof q.answer === 'string' && q.answer.trim() !== '') {
                        values = q.answer.split(';');
                    }

                    if (values.length === 0) {
                        multiCheckboxInvalid = true;
                    }
                }
            });

            if (multiCheckboxInvalid) {
                resolve(false);
                return;
            }
            
            if (!this.customQuestionsFormModel.valid) {
                reject(false);
                return;
            }

            const forkApis = [];

            const applicantExtendedAttributesArr = [];
            const applicationExtendedAttributesArr = [];


            /* Custom Questions*/
            const extendedAttributesArr = [];
            this.declarationQuestions.forEach(obj => {
                const extendedAttributesModel = new ExtendedAttributesModel();
                extendedAttributesModel.name = obj.question;
                extendedAttributesModel.value = obj.answer;
                extendedAttributesArr.push(extendedAttributesModel);
            });

            this.assetsQuestions.forEach(obj => {
                const extendedAttributesModel = new ExtendedAttributesModel();
                extendedAttributesModel.name = obj.question;
                extendedAttributesModel.value = obj.answer;
                extendedAttributesArr.push(extendedAttributesModel);
            });

            this.customQuestions.forEach(obj => {
                const extendedAttributesModel = new ExtendedAttributesModel();
                extendedAttributesModel.name = obj.question;
                if (obj.answer === true) {
                     extendedAttributesModel.value = '1'
                } else if (obj.answer === false) {
                     extendedAttributesModel.value = '0'
                } else if (obj.type === 'multi-checkbox' && Array.isArray(obj.answer)) {
                    extendedAttributesModel.value = obj.answer.join(';');
                } else {
                    extendedAttributesModel.value = obj.answer;
                }
                if (obj.visibility === 'APPLICANT') {
                    applicantExtendedAttributesArr.push(extendedAttributesModel);
                } else {
                    applicationExtendedAttributesArr.push(extendedAttributesModel);
                }
                if (obj.subQuestions) {
                    obj.subQuestions.forEach(sub => {
                        if (this.isAnswerMatched(obj.answer, sub.ifAns)) {
                            const subExtendedAttributesModel = new ExtendedAttributesModel();
                            subExtendedAttributesModel.name = sub.question;
                            if (sub.answer === true) {
                                subExtendedAttributesModel.value = '1';
                            }
                            else if (sub.answer === false) {
                                subExtendedAttributesModel.value = '0';
                            }
                            else if (sub.type === 'multi-checkbox' && Array.isArray(sub.answer)) {
                                subExtendedAttributesModel.value = sub.answer.join(';');
                            }
                            else {
                                subExtendedAttributesModel.value = sub.answer;
                            }
                            if (obj.visibility === 'APPLICANT') {
                                applicantExtendedAttributesArr.push(subExtendedAttributesModel);
                            } else {
                                applicationExtendedAttributesArr.push(subExtendedAttributesModel);
                            }
                            if (sub.subQuestions) {
                                sub.subQuestions.forEach(grandSub => {
                                    if (this.isAnswerMatched(sub.answer, grandSub.ifAns)) {
                                        const grandsubExtendedAttributesModel = new ExtendedAttributesModel();
                                        grandsubExtendedAttributesModel.name = grandSub.question;
                                        if (grandSub.answer === true) {
                                            grandsubExtendedAttributesModel.value = '1';
                                        }
                                        else if (grandSub.answer === false) {
                                            grandsubExtendedAttributesModel.value = '0';
                                        }
                                        else if (grandSub.type === 'multi-checkbox' && Array.isArray(grandSub.answer)) {
                                            grandsubExtendedAttributesModel.value = grandSub.answer.join(';');
                                        }
                                        else {
                                            grandsubExtendedAttributesModel.value = grandSub.answer;
                                        }
                                        if (obj.visibility === 'APPLICANT') {
                                            applicantExtendedAttributesArr.push(grandsubExtendedAttributesModel);
                                        } else {
                                            applicationExtendedAttributesArr.push(grandsubExtendedAttributesModel);
                                        }
                                    }
                                });
                            }
                        }
                    });
                }
            });

            const uniqueApplicationAttributes = applicationExtendedAttributesArr.filter((item, index, self) =>
                index === self.findIndex(t => t.name === item.name)
            );
            const uniqueApplicantAttributes = applicantExtendedAttributesArr.filter((item, index, self) =>
                index === self.findIndex(t => t.name === item.name)
            );

            if (uniqueApplicantAttributes.length > 0) {
                const extendedAttributesAPI = this.applicantService.addExtendedAttributesForApplicant(this.applicant.applicantId, uniqueApplicantAttributes);
                forkApis.push(extendedAttributesAPI);
                console.log('Adding applicant extended attributes API call:', uniqueApplicantAttributes);
            }

            if (uniqueApplicationAttributes.length > 0) {
                const applicationExtendedAttributesAPI = this.applicationService.addExtendedAttributes(this.keyIdentifier.applicationId, uniqueApplicationAttributes);
                forkApis.push(applicationExtendedAttributesAPI);
                console.log('Adding application extended attributes API call:', uniqueApplicationAttributes);
            }

            console.log('Total API calls to make:', forkApis.length);


            this.applicantConsent = forkJoin(forkApis).subscribe({
                next: () => {
                    console.log('Additional Question, declaration & asset liabilities added');
                    resolve(true);
                },
                error: (err: any) => {
                    console.error(err);
                    reject(false);
                }
            });
        });
    }

    getAlphabetFromNumber(j: any) {
        return String.fromCharCode(j + 65);
    }

    private shouldShowQuestion(question: any): boolean {
        // If visibility is APPLICANT, show for both primary and co-borrower
        if (question.visibility === 'APPLICANT') {
            return true;
        }
        // If no visibility specified (default to APPLICATION), show only for primary applicant
        return this.applicant?.applicantType === ApplicantType.PRIMARY;
    }

    onParentChange(parent: any) {
        if (parent.subQuestions) {
            parent.subQuestions.forEach(sub => {
                if (sub.type === 'multi-checkbox') {
                    sub.answer = [];
                } else {
                    sub.answer = "";
                }

                if (sub.subQuestions) {
                    sub.subQuestions.forEach(grandSub => {
                        if (grandSub.type === 'multi-checkbox') {
                            grandSub.answer = [];
                        } else {
                            grandSub.answer = "";
                        }
                    });
                }
            });
        }
    }

	isCheckboxSelected(answer: any, value: string): boolean {
		if (typeof answer === 'string') {
			return answer.split(';').includes(value);
		}
		return Array.isArray(answer) && answer.includes(value);
	}

    isAnswerMatched(answer: string, ifAns: string): boolean {
		if (!answer || !ifAns) {
            return false;
        }
        
		if (typeof answer === 'string' && answer.includes(';')) {
			return answer.split(';').includes(ifAns);
		}
		return answer === ifAns;
	}

	onCheckboxChange(question: any, value: string, checked: boolean) {
		let selectedValues = [];

		if (typeof question.answer === 'string' && question.answer) {
			selectedValues = question.answer.split(';');
		} else if (Array.isArray(question.answer)) {
			selectedValues = [...question.answer];
		}

		if (checked) {
			if (!selectedValues.includes(value)) {
				selectedValues.push(value);
			}
		} else {
			selectedValues = selectedValues.filter(item => item !== value);
		}

		question.answer = selectedValues.join(';');
	}

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        if (this.applicantConsent) {
            this.applicantConsent.unsubscribe();
        }
    }
}
